import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Package, Heart, LogOut, User, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ProductCard } from "@/components/store/ProductCard";
import { Button } from "@/components/ui/button";

const STATUS_COLOR = {
  "Delivered": "bg-green-100 text-green-700", "Shipped": "bg-blue-100 text-blue-700",
  "In Transit": "bg-blue-100 text-blue-700", "Payment Pending": "bg-amber-100 text-amber-700",
  "Awaiting Fulfillment": "bg-amber-100 text-amber-700", "Cancelled": "bg-red-100 text-red-700",
  "Refunded": "bg-zinc-100 text-zinc-600",
};

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { format } = useCurrency();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (user === false) nav("/login");
  }, [user, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
    api.get("/me/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Hi, {user.name?.split(" ")[0]}</h1>
          <p className="text-zinc-500 text-sm mt-1">{user.email}</p>
        </div>
        <Button variant="outline" onClick={() => { logout(); nav("/"); }} className="rounded-full" data-testid="logout-btn"><LogOut size={15} className="mr-2" />Sign out</Button>
      </div>

      <div className="flex gap-2 border-b border-zinc-200 mb-8">
        {[["orders", "Orders", Package], ["wishlist", "Wishlist", Heart], ["profile", "Profile", User]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setParams({ tab: k })}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500"}`}
                  data-testid={`tab-${k}`}>
            <Icon size={15} />{label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 && <p className="text-zinc-500 text-sm py-10 text-center">No orders yet. <Link to="/shop" className="underline">Start shopping</Link></p>}
          {orders.map((o) => (
            <Link to={`/track?order=${o.order_number}`} key={o.id} className="block border border-zinc-200 rounded-xl p-5 hover:border-zinc-400 transition-colors" data-testid={`order-${o.order_number}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-mono font-semibold">{o.order_number}</p>
                  <p className="text-xs text-zinc-500 mt-1">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} item(s)</p>
                </div>
                <div className="text-right">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLOR[o.status] || "bg-zinc-100 text-zinc-600"}`}>{o.status}</span>
                  <p className="font-semibold mt-2">{format(o.total)}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                {o.items.slice(0, 4).map((i, idx) => <img key={idx} src={i.image} alt="" className="w-12 h-14 object-cover rounded-md" />)}
              </div>
            </Link>
          ))}
        </div>
      )}

      {tab === "wishlist" && (
        wishlist.length === 0 ? <p className="text-zinc-500 text-sm py-10 text-center">Your wishlist is empty.</p> :
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
          {wishlist.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}

      {tab === "profile" && (
        <div className="max-w-xl space-y-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-zinc-100 py-3"><span className="text-zinc-500">Name</span><span className="font-medium">{profile?.name || user.name}</span></div>
            <div className="flex justify-between border-b border-zinc-100 py-3"><span className="text-zinc-500">Email</span><span className="font-medium">{user.email}</span></div>
            <div className="flex justify-between border-b border-zinc-100 py-3"><span className="text-zinc-500">Phone</span><span className="font-medium">{profile?.phone || "—"}</span></div>
            <div className="flex justify-between border-b border-zinc-100 py-3"><span className="text-zinc-500">Account type</span><span className="font-medium capitalize">{user.kind}</span></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm flex items-center gap-2"><MapPin size={15} />Delivery addresses</h3>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.dispatchEvent(new Event("open-kyc"))} data-testid="add-address-btn">
                {profile?.addresses?.length ? "Add address" : "Complete profile"}
              </Button>
            </div>
            {profile?.addresses?.length ? (
              <div className="space-y-3">
                {profile.addresses.map((a) => (
                  <div key={a.id} className="border border-zinc-200 rounded-xl p-4 text-sm" data-testid={`address-${a.id}`}>
                    <p className="font-medium">{a.full_name} {a.is_default && <span className="text-[10px] bg-zinc-900 text-white px-2 py-0.5 rounded-full ml-1">Default</span>}</p>
                    <p className="text-zinc-600 mt-1">{a.street}, {a.city}, {a.state}, {a.country} {a.postal_code}</p>
                    <p className="text-zinc-500 text-xs mt-1">{a.phone}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-sm border border-dashed border-zinc-200 rounded-xl p-6 text-center">No delivery address yet. Add one to speed up checkout.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
